import {
  Alert,
  AlertIcon,
  Button,
  ButtonGroup,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Portal,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { ExpiryReason } from '@pelicargo/types';
import { MULTI_KEY } from '@pelicargo/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldArrayWithId, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import {
  HiArchiveBoxArrowDown,
  HiArrowRight,
  HiArrowTurnDownLeft,
  HiArrowTurnDownRight,
  HiChevronDown,
  HiEllipsisVertical,
  HiTrash,
} from 'react-icons/hi2';

import { trpc, TrpcOutputs } from '../config/trpc';
import { API } from '../utils/typings';
import { AirportInput } from './Autocomplete/AirportInput';
import { RouteEditorSegmentModal } from './RouteEditorSegmentModal';
import { SimpleForm } from './SimpleForm';

type Route = API.SearchRoutes['routes'][0];

type AirportStop = {
  position: number;
  value: string;
  ids: string[];
};

type FormValues = {
  airports: AirportStop[];
  routeIds: number[];
};

export const RouteEditorPathForm = ({ routes, refetch }: { routes: Route[]; refetch: () => Promise<any> }) => {
  const routesAllSameLength = useMemo(() => {
    if (!routes.length) return true;

    const baseLength = routes[0].path.split('-').length;
    const allSameLength = routes.every((route) => route.path.split('-').length === baseLength);

    return allSameLength;
  }, [routes]);

  const routeIds = useMemo(() => routes.map((r) => r.id), [routes]);

  const initialAirports = useMemo<AirportStop[]>(() => {
    const routeArrs = routes.flatMap((r) => {
      const segments = r.path.split('-');
      return segments.map((airport, i) => ({ id: airport, position: i }));
    });

    const airportDict = routeArrs.reduce(
      (acc, curr) => {
        // ensure we have an entry for the position
        acc[curr.position] ??= { ids: [curr.id], value: curr.id, position: curr.position };

        // if the current airport id is not already in the list, add it
        if (!acc[curr.position].ids.includes(curr.id)) acc[curr.position].ids.push(curr.id);

        // if more than one airport id, set value to [Multi]
        if (acc[curr.position].ids.length > 1) acc[curr.position].value = MULTI_KEY;
        return acc;
      },
      {} as Record<number, AirportStop>,
    );

    return Object.values(airportDict);
  }, [routes]);

  const routeEditorMethods = useForm<FormValues>({
    defaultValues: { airports: initialAirports ?? [], routeIds: routeIds ?? [] },
  });
  const { reset, getValues, setValue, watch } = routeEditorMethods;

  const handleResetForm = useCallback(() => {
    reset({ airports: initialAirports, routeIds });
  }, [initialAirports, routeIds, reset]);

  useEffect(() => {
    handleResetForm();
  }, [handleResetForm, routeIds]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      // Skip if the changes are not for airports.*.value
      if (!name?.match(/airports\.\d+\.value/)) return;

      const index = parseInt(name.split('.')[1]);
      const currentValue = value.airports?.[index];

      // Update the IDs array to contain only the new value value
      if (currentValue?.value !== MULTI_KEY) setValue(`airports.${index}.ids`, [currentValue.value]);
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  const { mutateAsync: updateRoutes, isLoading: isLoadingUpdateRoutes } = trpc.updateRoutes.useMutation();
  const { mutateAsync: expireRoutes, isLoading: isLoadingExpireRoutes } = trpc.expireRoutes.useMutation();

  const handleExpireRoute = (reason: ExpiryReason) => async () => {
    await expireRoutes({ route_ids: routeIds, reason });
    await refetch();
  };

  const segmentModalDisclosure = useDisclosure();
  const [createdLegs, setCreatedLegs] = useState<TrpcOutputs['updateRoutes']>();

  const onValid = async (data: FormValues) => {
    const { airports, routeIds: route_ids } = data;
    const legs = await updateRoutes({ airports, route_ids });

    if (!legs?.length) return await refetch();
    setCreatedLegs(legs);
    segmentModalDisclosure.onOpen();
  };

  const { fields } = useFieldArray<FormValues, 'airports', '_id'>({
    keyName: '_id',
    control: routeEditorMethods.control,
    name: 'airports',
  });

  const onAddAirport = useCallback(
    (desiredIndex: number) => {
      const currentAirports = getValues('airports');
      const newAirport = { ids: [], value: '???', position: desiredIndex };

      // Position new airport into the nextAirports array
      const nextAirports = currentAirports
        ?.flatMap((airport, index) => {
          if (index === desiredIndex) return [newAirport, airport];
          return [airport];
        })
        ?.map((node, index) => ({ ...node, position: index }));

      // If it's the last index, push the new airport to the end since it wasn't added yet
      if (desiredIndex === nextAirports.length) nextAirports.push(newAirport);

      // Set the value
      setValue('airports', nextAirports, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    },
    [setValue, getValues],
  );

  const onRemoveAirport = useCallback(
    (indexToRemove: number) => {
      const currentAirports = getValues('airports');
      const nextAirports = currentAirports.filter((node) => node.position !== indexToRemove);

      setValue('airports', nextAirports, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    },
    [setValue, getValues],
  );

  const renderItem = useCallback(
    (
      field: FieldArrayWithId<FormValues, 'airports', '_id'>,
      index: number,
      arr: FieldArrayWithId<FormValues, 'airports', '_id'>[],
    ) => {
      return (
        <RouteEditorAirportItem
          key={field._id}
          append={onAddAirport}
          remove={onRemoveAirport}
          prefix={`airports.${index}`}
          index={index}
          isLast={index === arr.length - 1}
        />
      );
    },
    [onAddAirport, onRemoveAirport],
  );

  if (!routesAllSameLength) {
    return (
      <Stack w="full" spacing={4}>
        <Stack direction="row" spacing="4" justify="space-between">
          <Heading variant="h5">Routing</Heading>
        </Stack>
        <Alert status="error" borderRadius="md" borderWidth="1px" borderColor="red.300" colorScheme="red">
          <AlertIcon />
          Selected paths have a different number of segments.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack w="full">
      <RouteEditorSegmentModal
        {...segmentModalDisclosure}
        onClose={() => {
          segmentModalDisclosure.onClose();
          refetch();
        }}
        legs={createdLegs}
      />

      <SimpleForm submitOnBlur={false} {...(routeEditorMethods as any)}>
        <form onSubmit={routeEditorMethods.handleSubmit(onValid)}>
          <Stack direction="row" spacing="4" justify="space-between">
            <Heading variant="h5">Routing</Heading>
            <Stack direction="row" alignItems="center" spacing="4">
              <Button onClick={handleResetForm} colorScheme="secondary" variant="ghost">
                Reset
              </Button>
              <ButtonGroup isAttached>
                <Button colorScheme="primary" isLoading={isLoadingUpdateRoutes} position="sticky" type="submit">
                  Save & Ignore History
                </Button>
                <Menu>
                  <MenuButton
                    as={IconButton}
                    colorScheme="primary"
                    isLoading={isLoadingExpireRoutes}
                    position="sticky"
                    icon={<HiChevronDown />}
                  />
                  <MenuList>
                    <MenuGroup title="Expiry Options">
                      <MenuItem icon={<HiArchiveBoxArrowDown />} onClick={handleExpireRoute(ExpiryReason.ROUTE_ENDED)}>
                        Mark Route-Ended
                      </MenuItem>
                      <MenuItem icon={<HiTrash />} onClick={handleExpireRoute(ExpiryReason.INVALID_ENTRY)}>
                        Mark Invalid Entry
                      </MenuItem>
                    </MenuGroup>
                  </MenuList>
                </Menu>
              </ButtonGroup>
            </Stack>
          </Stack>
          <Stack direction="row" spacing="2">
            {fields?.map(renderItem)}
          </Stack>
        </form>
      </SimpleForm>
    </Stack>
  );
};

const RouteEditorAirportItem = ({
  prefix,
  append,
  remove,
  index,
  isLast,
}: {
  prefix: `airports.${number}`;
  append: (desiredPosition: number) => void;
  remove: (index: number) => void;
  index: number;
  isLast: boolean;
}) => {
  const { watch } = useFormContext<FormValues>();
  const newField = watch(prefix);

  return (
    <Stack direction="row" alignItems="center">
      <AirportInput name={`${prefix}.value`} />
      <Menu>
        <MenuButton
          as={IconButton}
          icon={isLast ? <HiEllipsisVertical /> : <HiArrowRight />}
          variant="ghost"
          aria-label="More Options"
        />
        <Portal>
          <MenuList>
            <MenuGroup title={`Modify`}>
              <MenuItem icon={<HiTrash />} onClick={() => remove(index)}>
                Remove {newField.value}
              </MenuItem>
            </MenuGroup>
            <MenuGroup title="Add">
              <MenuItem icon={<HiArrowTurnDownLeft />} onClick={() => append(index)}>
                Add before {newField.value}
              </MenuItem>
              <MenuItem icon={<HiArrowTurnDownRight />} onClick={() => append(index + 1)}>
                Add after {newField.value}
              </MenuItem>
            </MenuGroup>
            <MenuGroup title="Airports">
              <MenuItem isDisabled maxW="300px">
                <Text fontSize="xs">{newField?.ids?.join(', ')}</Text>
              </MenuItem>
            </MenuGroup>
          </MenuList>
        </Portal>
      </Menu>
    </Stack>
  );
};
